// src/pages/VendaMesLoja.jsx
import React, { useState, useEffect } from "react";
import { Modal, Button, Table, Collapse, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { api } from "../../services/api.js";
import Swal from "sweetalert2";
import "./flash.css";
import {
  FaThumbsUp,
  FaArrowDown,
  FaChevronDown,
  FaChevronUp,
  FaFilter,
  FaArrowUp,
} from "react-icons/fa";
import Menu from "../../comum/Menu/index.jsx";
import Footer from "../../comum/Footer/index.jsx";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
} from "recharts";

// Modal de Loading
function LoadingModal({ show, message }) {
  if (!show) return null;
  return (
    <div className="loading-modal-overlay">
      <div className="loading-modal-content">
        <div
          className="spinner-border text-primary"
          role="status"
          style={{ width: "3rem", height: "3rem" }}
        >
          <span className="visually-hidden">Loading...</span>
        </div>
        <div style={{ marginTop: 15, fontSize: 18 }}>{message}</div>
      </div>
      <style>{`
        .loading-modal-overlay {
            position: fixed;
            top: 0; left: 0; right: 0; bottom: 0;
            background: rgba(0,0,0,0.5);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 1050;
        }
        .loading-modal-content {
            background: white;
            padding: 20px 40px;
            border-radius: 8px;
            display: flex;
            flex-direction: column;
            align-items: center;
            box-shadow: 0 0 10px rgba(0,0,0,0.25);
        }
      `}</style>
    </div>
  );
}

// Custom Tooltip para os gráficos
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="custom-tooltip bg-white p-2 border rounded shadow">
        <p className="label">{`Dia: ${label}`}</p>
        <p className="intro" style={{ color: "#8884d8" }}>{`Venda: ${formatMoeda(data.venda)}`}</p>
        <p className="intro" style={{ color: "#82ca9d" }}>{`Meta: ${formatMoeda(data.meta)}`}</p>
        <p className="intro" style={{ color: "#ff7300" }}>{`% Atingido: ${formatPercent(data.atingido)}`}</p>
        <p className="intro" style={{ color: "#8884d8" }}>{`Margem: ${formatPercent(data.margem)}`}</p>
      </div>
    );
  }
  return null;
};

// Formata moeda BR
const formatMoeda = (valor) =>
  valor?.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

// Formata percentual BR
const formatPercent = (valor) => {
  if (isNaN(valor) || valor === null || valor === undefined) {
    return "N/A";
  }
  return `${Number(valor).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} %`;
};

const VendaMesLoja = () => {
  const [showModal, setShowModal] = useState(true);
  const [showLojaModal, setShowLojaModal] = useState(false);
  const [mesSelecionado, setMesSelecionado] = useState("");
  const [dadosMes, setDadosMes] = useState([]);
  const [detalhesLoja, setDetalhesLoja] = useState({});
  const [loading, setLoading] = useState(false);
  const [lojas, setLojas] = useState([]);
  const [lojasSelecionadas, setLojasSelecionadas] = useState([]);
  const [totalData, setTotalData] = useState({});
  const [labelAngle, setLabelAngle] = useState(0);

  const navigate = useNavigate();

  // Verifica acesso
  useEffect(() => {
    const usuario = localStorage.getItem("chapa");
    const permiteVendas = localStorage.getItem("permiteVendas");

    if (!usuario) {
      navigate("/arbor/login");
      return;
    }

    if (permiteVendas !== "S") {
      Swal.fire({
        icon: "error",
        title: "Acesso Negado",
        text: "Não é permitido acessar as vendas.",
        confirmButtonText: "OK",
      }).then(() => {
        navigate("/arbor/dadosfunc");
      });
    }
  }, [navigate]);

  // Mês atual
  useEffect(() => {
    const hoje = new Date();
    const aa = String(hoje.getFullYear()).slice(2);
    const mm = String(hoje.getMonth() + 1).padStart(2, "0");
    setMesSelecionado(`${aa}${mm}`);
  }, []);

  // Lógica para rotação de labels em mobile
  useEffect(() => {
    const handleResize = () => {
      setLabelAngle(window.innerWidth < 768 ? -45 : 0);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Carregar lista de lojas
  useEffect(() => {
    const fetchLojas = async () => {
      try {
        const { data } = await api.get("/lojas/localizacao");
        const todasLojas = data.map((loja) =>
          String(loja.cod_emp).padStart(2, "0")
        );
        setLojas(data);
        setLojasSelecionadas(todasLojas);
      } catch (e) {
        console.error("Erro ao carregar lojas:", e);
        setLojas([]);
      }
    };
    fetchLojas();
  }, []);

  // Carregar totais por loja
  const carregarMes = async (mes, lojas) => {
    const usuario = localStorage.getItem("chapa");
    if (!mes || !usuario) return;

    setLoading(true);
    const lojasParam = lojas.length > 0 ? lojas.join(",") : "";
    try {
      const { data } = await api.get(
        `venda/mes/loja/total?mes=${mes}&usuario=${usuario}${
          lojasParam ? `&busca=${lojasParam}` : ""
        }`
      );
      const dados = Array.isArray(data) ? data : [];
      setDadosMes(dados);

      if (dados.length > 0) {
        const totalVenda = dados.reduce((sum, item) => sum + (item.venda || 0), 0);
        const totalMeta = dados.reduce((sum, item) => sum + (item.meta || 0), 0);
        const totalMargem = dados.reduce((sum, item) => sum + (item.margem || 0), 0);
        const totalAtingido = (totalVenda / totalMeta) * 100;

        setTotalData({
          venda: totalVenda,
          meta: totalMeta,
          margem: totalMargem / dados.length,
          atingido: totalAtingido,
        });
      } else {
        setTotalData({});
      }

      setDetalhesLoja({});
    } catch (e) {
      console.error("Erro carregando total loja:", e);
      setDadosMes([]);
      setTotalData({});
    }
    setLoading(false);
  };

  // Carregar dias de uma loja
  const carregarDiasLoja = async (codLoja) => {
    const usuario = localStorage.getItem("chapa");
    if (!usuario) return;

    setLoading(true);
    if (detalhesLoja[codLoja]) {
      const copia = { ...detalhesLoja };
      delete copia[codLoja];
      setDetalhesLoja(copia);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get(
        `venda/mes/acum?mes=${mesSelecionado}&usuario=${usuario}&busca=${codLoja}`
      );
      const formattedData = data.map((item) => ({
        ...item,
        atingido: (item.venda / item.meta) * 100,
      }));
      setDetalhesLoja({ ...detalhesLoja, [codLoja]: formattedData });
    } catch (e) {
      console.error("Erro carregando dias da loja:", e);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (mesSelecionado && lojasSelecionadas.length > 0) {
      carregarMes(mesSelecionado, lojasSelecionadas);
    }
  }, [mesSelecionado, lojasSelecionadas]);

  const handleMesChange = (valor) => {
    setMesSelecionado("");
    if (!valor) return;
    const [ano, mes] = valor.split("-");
    const aa = ano.slice(2);
    setMesSelecionado(`${aa}${mes}`);
  };

  const handleLojaChange = (cod_emp) => {
    setLojasSelecionadas((prevLojas) => {
      if (prevLojas.includes(cod_emp)) {
        return prevLojas.filter((loja) => loja !== cod_emp);
      } else {
        return [...prevLojas, cod_emp];
      }
    });
  };

  const handleSelectAllLojas = () => {
    const todasLojas = lojas.map((loja) =>
      String(loja.cod_emp).padStart(2, "0")
    );
    setLojasSelecionadas(todasLojas);
  };

  const handleClearAllLojas = () => {
    setLojasSelecionadas([]);
  };

  // Dados para gráfico geral de % atingido
  const chartAtingido = Array.isArray(dadosMes)
    ? dadosMes.map((l) => ({
        filial: String(l.filial).padStart(2, "0"),
        atingido: Number(l.atingido) || 0,
        venda: l.venda,
        meta: l.meta,
        margem: Number(l.margem) || 0, // Incluindo a margem aqui
      }))
    : [];

  // Adiciona a barra de total ao final
  if (totalData.atingido !== undefined) {
    chartAtingido.push({
      filial: "Total",
      atingido: totalData.atingido,
      venda: totalData.venda,
      meta: totalData.meta,
      margem: totalData.margem, // Adicionando a margem para a barra de Total
    });
  }

  // Novo componente CustomTooltip para o gráfico geral
  const CustomTooltipGeral = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="custom-tooltip bg-white p-2 border rounded shadow">
          <p className="label">{`Loja: ${label}`}</p>
          <p className="intro" style={{ color: "#8884d8" }}>{`Venda: ${formatMoeda(data.venda)}`}</p>
          <p className="intro" style={{ color: "#82ca9d" }}>{`Meta: ${formatMoeda(data.meta)}`}</p>
          <p className="intro" style={{ color: "#ff7300" }}>{`% Atingido: ${formatPercent(data.atingido)}`}</p>
          <p className="intro" style={{ color: "#ff7300" }}>{`Margem: ${formatPercent(data.margem)}`}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div>
      <Menu modulo="ARBOR" />
      <div className="p-3 container-fluid">
        <h3 className="text-center mb-3">
          Consulta de Vendas por Loja - {mesSelecionado}
        </h3>

        {/* Informação de Atingimento Total para Mobile */}
        {totalData.atingido !== undefined && (
          <div className="text-center d-block d-sm-none my-3">
            <div className="rotate-on-mobile">
              <h5 className="mb-0">Atingimento Total</h5>
              <span className="h4 fw-bold">
                {formatPercent(totalData.atingido)}
              </span>
              {totalData.atingido >= 100 ? (
                <FaArrowUp className="text-success ms-2" />
              ) : (
                <FaArrowDown className="text-danger ms-2" />
              )}
            </div>
          </div>
        )}

        {/* Modal Seleção Mês */}
        <Modal show={showModal} onHide={() => setShowModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Selecionar Mês</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <input
              type="month"
              className="form-control"
              value={
                mesSelecionado
                  ? `${"20" + mesSelecionado.slice(0, 2)}-${mesSelecionado.slice(
                      2
                    )}`
                  : ""
              }
              onChange={(e) => handleMesChange(e.target.value)}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Fechar
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setShowModal(false);
                carregarMes(mesSelecionado, lojasSelecionadas);
              }}
            >
              Carregar
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Modal Seleção de Lojas */}
        <Modal
          show={showLojaModal}
          onHide={() => setShowLojaModal(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Selecionar Lojas</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: "400px", overflowY: "auto" }}>
            <div className="d-flex justify-content-between mb-3">
              <Button size="sm" onClick={handleSelectAllLojas}>
                Selecionar Todas
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={handleClearAllLojas}
              >
                Limpar Todas
              </Button>
            </div>
            <Form>
              {lojas.map((loja) => (
                <Form.Check
                  key={loja.cod_emp}
                  type="checkbox"
                  label={`Filial ${String(loja.cod_emp).padStart(
                    2,
                    "0"
                  )} - ${loja.razao}`}
                  checked={lojasSelecionadas.includes(
                    String(loja.cod_emp).padStart(2, "0")
                  )}
                  onChange={() =>
                    handleLojaChange(String(loja.cod_emp).padStart(2, "0"))
                  }
                />
              ))}
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowLojaModal(false)}>
              Fechar
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setShowLojaModal(false);
                carregarMes(mesSelecionado, lojasSelecionadas);
              }}
            >
              Aplicar Filtro
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Input de mês e botões */}
        <div className="mb-3 d-flex flex-column flex-sm-row gap-2 align-items-center">
          <label className="me-2 mb-0">Mês/Ano:</label>
          <input
            type="month"
            className="form-control"
            style={{ maxWidth: "200px" }}
            value={
              mesSelecionado
                ? `${"20" + mesSelecionado.slice(0, 2)}-${mesSelecionado.slice(
                    2
                  )}`
                : ""
            }
            onChange={(e) => handleMesChange(e.target.value)}
          />
          <div className="d-flex gap-2">
            <Button
              onClick={() => carregarMes(mesSelecionado, lojasSelecionadas)}
              disabled={loading}
            >
              {loading ? "Carregando..." : "Buscar"}
            </Button>
            <Button variant="primary" onClick={() => setShowLojaModal(true)}>
              <FaFilter className="me-1" /> Lojas
            </Button>
          </div>
        </div>

        {/* Gráfico comparativo de % atingido geral */}
        {chartAtingido.length > 0 && (
          <>
            <div style={{ width: "100%", height: 320 }} className="mb-3">
              <h5 className="text-center">Venda x Meta (%)</h5>
              <ResponsiveContainer>
                <BarChart
                  data={chartAtingido}
                  margin={{ top: 20, right: 20, left: 10, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="filial" />
                  <YAxis tickFormatter={formatPercent} />
                  <Tooltip content={<CustomTooltipGeral />} />

                  

                  
                  <Bar dataKey="atingido" name="% Atingido" fill="#8884d8">
                    <LabelList
                      dataKey="atingido"
                      position="top"
                      formatter={formatPercent}
                      style={{ fontSize: 12, fill: "#888" }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            {/* NOVO: Gráfico comparativo de Margem total */}
            <div style={{ width: "100%", height: 320 }} className="mb-3">
              <h5 className="text-center">Margem (%)</h5>
              <ResponsiveContainer>
                <BarChart
                  data={chartAtingido}
                  margin={{ top: 20, right: 20, left: 10, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="filial" />
                  <YAxis tickFormatter={formatPercent} />
                  <Tooltip content={<CustomTooltipGeral />} />
                  <Bar dataKey="margem" name="Margem (%)" fill="#82ca9d">
                    <LabelList
                      dataKey="margem"
                      position="top"
                      formatter={formatPercent}
                      style={{ fontSize: 12, fill: "#888" }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        )}

        {/* Tabela principal por loja */}
        <div className="table-responsive mt-4">
          <Table striped bordered hover size="sm" className="text-center">
            <thead>
              <tr>
                <th>Loja</th>
                <th>Venda</th>
                <th>Meta</th>
                <th>Margem (%)</th>
                <th>Atingido (%)</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(dadosMes) && dadosMes.length > 0 ? (
                dadosMes.map((loja) => (
                  <React.Fragment key={loja.filial}>
                    <tr>
                      <td>{String(loja.filial).padStart(2, "0")}</td>
                      <td>{formatMoeda(loja.venda)}</td>
                      <td>{formatMoeda(loja.meta)}</td>
                      <td>{formatPercent(loja.margem)}</td>
                      <td>
                        {formatPercent(loja.atingido)}
                        {loja.atingido >= 100 ? (
                          <FaThumbsUp className="text-success ms-2" />
                        ) : (
                          <FaArrowDown className="text-danger ms-2" />
                        )}
                      </td>
                      <td>
                        <Button
                          size="sm"
                          onClick={() => carregarDiasLoja(loja.filial)}
                        >
                          {detalhesLoja[loja.filial] ? (
                            <FaChevronUp />
                          ) : (
                            <FaChevronDown />
                          )}
                        </Button>
                      </td>
                    </tr>
                    <tr>
                      <td colSpan="6" className="p-0 border-0">
                        <Collapse in={!!detalhesLoja[loja.filial]}>
                          <div className="p-2">
                            {detalhesLoja[loja.filial] && (
                              <>
                                {/* Tabela detalhe por dia */}
                                <Table
                                  striped
                                  bordered
                                  hover
                                  size="sm"
                                  className="mb-3"
                                >
                                  <thead>
                                    <tr>
                                      <th>Dia</th>
                                      <th>Venda</th>
                                      <th>Meta</th>
                                      <th>Margem (%)</th>
                                      <th>Atingido</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {detalhesLoja[loja.filial].map((dia, i) => (
                                      <tr key={i}>
                                        <td>{dia.numdia}</td>
                                        <td>{formatMoeda(dia.venda)}</td>
                                        <td>{formatMoeda(dia.meta)}</td>
                                        <td>{formatPercent(dia.margem)}</td>
                                        <td>
                                          {formatPercent(dia.atingido)}
                                          {dia.atingido >= 100 ? (
                                            <FaArrowUp className="text-success ms-2" />
                                          ) : (
                                            <FaArrowDown className="text-danger ms-2" />
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>

                                {/* Gráfico Margem (%) */}
                                <div
                                  style={{
                                    width: "100%",
                                    height: 260,
                                    marginBottom: "2rem",
                                  }}
                                >
                                  <h5 className="text-center">
                                    Evolução da Margem (%)
                                  </h5>
                                  <ResponsiveContainer>
                                    <LineChart data={detalhesLoja[loja.filial]}>
                                      <CartesianGrid strokeDasharray="3 3" />
                                      <XAxis
                                        dataKey="numdia"
                                        angle={labelAngle}
                                        tick={{ fontSize: 12 }}
                                        textAnchor="end"
                                        height={50}
                                      />
                                      <YAxis
                                        tickFormatter={formatPercent}
                                        domain={["auto", "dataMax"]}
                                      />
                                      <Tooltip content={<CustomTooltip />} />
                                      <Legend />
                                      <Line
                                        type="monotone"
                                        dataKey="margem"
                                        stroke="#82ca9d"
                                        name="Margem (%)"
                                        dot={{ r: 2 }}
                                      >
                                        <LabelList
                                          dataKey="margem"
                                          position="top"
                                          formatter={formatPercent}
                                          style={{ fontSize: 12, fill: "#888" }}
                                        />
                                      </Line>
                                    </LineChart>
                                  </ResponsiveContainer>
                                </div>

                                {/* Gráfico % Atingido */}
                                <div style={{ width: "100%", height: 260 }}>
                                  <h5 className="text-center">
                                    Venda x Meta do mês (%)
                                  </h5>
                                  <ResponsiveContainer>
                                    <BarChart
                                      data={detalhesLoja[loja.filial]}
                                    >
                                      <CartesianGrid strokeDasharray="3 3" />
                                      <XAxis
                                        dataKey="numdia"
                                        angle={labelAngle}
                                        tick={{ fontSize: 12 }}
                                        textAnchor="end"
                                        height={50}
                                      />
                                      <YAxis
                                        tickFormatter={formatPercent}
                                        domain={["auto", "dataMax"]}
                                      />
                                      <Tooltip content={<CustomTooltip />} />
                                      <Legend />
                                      <Bar
                                        dataKey="atingido"
                                        name="% Atingido"
                                        fill="#8884d8"
                                      >
                                        <LabelList
                                          dataKey="atingido"
                                          position="top"
                                          formatter={formatPercent}
                                          style={{ fontSize: 12, fill: "#888" }}
                                        />
                                      </Bar>
                                    </BarChart>
                                  </ResponsiveContainer>
                                </div>
                              </>
                            )}
                          </div>
                        </Collapse>
                      </td>
                    </tr>
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan="6">Nenhum dado disponível.</td>
                </tr>
              )}
            </tbody>
            {totalData.atingido !== undefined && (
              <tfoot>
                <tr className="fw-bold table-dark">
                  <td>Total</td>
                  <td>{formatMoeda(totalData.venda)}</td>
                  <td>{formatMoeda(totalData.meta)}</td>
                  <td>{formatPercent(totalData.margem)}</td>
                  <td>
                    {formatPercent(totalData.atingido)}
                    {totalData.atingido >= 100 ? (
                      <FaArrowUp className="text-success ms-2" />
                    ) : (
                      <FaArrowDown className="text-danger ms-2" />
                    )}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </Table>
        </div>
      </div>
      <Footer className="footer navbar-fixed-bottom" />
      <LoadingModal show={loading} message="Carregando..." />
    </div>
  );
};

export default VendaMesLoja;